import React, { useState } from 'react'
import { Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

interface Props {
  /**
   * Path to the CSV endpoint, relative to the API base (e.g.
   * `/analytics/export/overview.csv`).
   */
  endpoint: string
  /** Query params forwarded to the endpoint. */
  params?: Record<string, string | number | boolean | undefined>
  /** Base name for the downloaded file (a `-YYYY-MM-DD.csv` suffix is appended). */
  filename: string
  /** Optional label (defaults to "Export CSV"). */
  label?: string
  className?: string
}

/**
 * Admin-only CSV download button. Returns `null` for analysts so the UI stays
 * clean for read-only users. Streams the response as a Blob and triggers a
 * client-side download via an anchor click.
 */
export function ExportButton({
  endpoint,
  params,
  filename,
  label = 'Export CSV',
  className = '',
}: Props) {
  const { canExport } = useAuth()
  const [loading, setLoading] = useState(false)

  if (!canExport) return null

  const handleClick = async () => {
    setLoading(true)
    try {
      const res = await api.get(endpoint, {
        params,
        responseType: 'blob',
      })
      const stamp = new Date().toISOString().slice(0, 10)
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${filename}-${stamp}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('CSV downloaded')
    } catch (err: any) {
      console.error('CSV export failed', err)
      toast.error('Export failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
    >
      <Download className="w-4 h-4" />
      {loading ? 'Exporting…' : label}
    </button>
  )
}
